const stripHtmlTags = (value = "") =>
  String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const stripLabelPrefixes = (value = "", labels = []) => {
  const text = stripHtmlTags(value);
  if (!text) return "";
  const lower = text.toLowerCase();
  for (const prefix of labels) {
    const normalized = String(prefix || "").toLowerCase();
    if (normalized && lower.startsWith(normalized)) {
      return text
        .slice(prefix.length)
        .replace(/^[:\s-]+/, "")
        .trim();
    }
  }
  return text.trim();
};

const removeTrailingCm = (value = "") =>
  String(value || "").replace(/\s*cm$/i, "").trim();

const FOOTWEAR_SIZE_PREFIXES = [
  "Mărime încălțăminte",
  "Marime incaltaminte",
  "Mărime",
  "Marime",
  "Size",
];
const FOOTWEAR_OBS_PREFIXES = [
  "Observații mărime",
  "Observatii marime",
  "Observații marimi",
  "Observatii marimi",
];

const sanitizeFootwearSizeValue = (value = "") =>
  removeTrailingCm(stripLabelPrefixes(value, FOOTWEAR_SIZE_PREFIXES));

const sanitizeFootwearObservationValue = (value = "") =>
  stripLabelPrefixes(value, [
    ...FOOTWEAR_OBS_PREFIXES,
    ...FOOTWEAR_SIZE_PREFIXES,
  ]);

const plainValueOrDash = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed ? trimmed : "—";
};

const buildHtmlContent = ({
  conditionValue,
  conditionLabel = "",
  conditionText = "",
  customDescription,
  exteriorColor,
  interiorColor,
  exteriorMaterial,
  interiorMaterial,
  includedItems,
  hardwareColor,
  designerId,
  entrupyId,
  location,
  length,
  height,
  depth,
  detailedDescription,
  sortlyId,
  salePrice,
  price,
  brand,
  layawayPlan,
  itemSize = "",
  sizeValue = "",
  apparelMeasurements = [],
  isFootwear,
  isAccessoryScarf = false,
  isAccessoryHat = false,
  footwearSizeNote = "",
}) => {
  const formatDimensionValue = (value) => {
    if (value === null || value === undefined) return "";
    const raw = String(value).trim();
    if (!raw) return "";
    return /cm$/i.test(raw) ? raw : `${raw} cm`;
  };
  const dimensionOrDash = (value) => {
    const formatted = formatDimensionValue(value);
    return formatted || "—";
  };

  const cleanedItemSize = isFootwear
    ? sanitizeFootwearSizeValue(itemSize)
    : stripHtmlTags(itemSize);
  const cleanedSizeValue = stripHtmlTags(sizeValue);
  const cleanedFootwearNote = isFootwear
    ? sanitizeFootwearObservationValue(footwearSizeNote)
    : stripHtmlTags(footwearSizeNote);

  let layawayHtml = "";
  const totalPrice = salePrice || price;
  const formatMaterialGroup = (value) => {
    const entries = String(value || "")
      .split("\n")
      .map((entry) => entry.trim())
      .filter(Boolean);
    return entries.length
      ? entries.map((entry) => `<div>${entry}</div>`).join("")
      : "";
  };
  const exteriorMaterialBlock = formatMaterialGroup(exteriorMaterial);
  const interiorMaterialBlock = formatMaterialGroup(interiorMaterial);

  if (layawayPlan) {
    let advance, rate1, rate2, rate3;

    if (layawayPlan === "1 Rata Speciala") {
      const specialPercent = 0.5;
      const percentLabel = `${Math.round(specialPercent * 100)}%`;
      advance = Math.round(totalPrice * specialPercent);
      rate1 = Math.round(totalPrice - advance);
      layawayHtml = `
[vc_toggle title="Doresc produsul prin LAYAWAY" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790924554{margin-top: -30px !important;}"]
<table style="height: 133px;">
    <tbody>
        <tr>
            <td style="width: 25.0000%;">
                <div style="text-align: center;"><span style="color: #000000;"><strong><span style="font-family: Symbol; font-size: 14px;">Avans ${percentLabel}</span></strong></span></div>
            </td>
            <td style="width: 24.8855%;">
                <div style="text-align: center;"><span style="font-family: Symbol; color: #000000;"><span style="font-size: 14px;"><strong>Rata 1 ${percentLabel}</strong></span></span></div>
            </td>
        </tr>
        <tr>
            <td style="width: 25.0000%;">
                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${advance} RON</span></div>
            </td>
            <td style="width: 24.8855%;">
                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${rate1} RON</span></div>
            </td>
        </tr>
    </tbody>
</table>
<p style="text-align: center;">COMISION <strong>0%</strong></p>
<p style="text-align: center;" align="center">Acum plătești doar avansul iar noi o să te contactăm în maxim 4 ore pentru semnarea contractului. Totul este online și rapid.</p>
[/vc_toggle]
`.trim();
    } else {
      advance = Math.round(totalPrice * 0.1);
      const remaining = Math.round(totalPrice - advance);
      rate1 = remaining;
      rate2 = Math.round(remaining / 2);
      rate3 = Math.round(remaining / 3);

      const rateColumns = [
        { label: "Rata 1", value: rate1 },
      ];
      if (layawayPlan === "2 Rate" || layawayPlan === "3 Rate") {
        rateColumns.push({ label: "Rata 2", value: rate2 });
      }
      if (layawayPlan === "3 Rate") {
        rateColumns.push({ label: "Rata 3", value: rate3 });
      }

      const rateHeaderCells = rateColumns
        .map(
          (col) => `
            <td style="width: 24.8855%;">
                <div style="text-align: center;"><span style="font-family: Symbol; color: #000000;"><span style="font-size: 14px;"><strong>${col.label}</strong></span></span></div>
            </td>`
        )
        .join("");

      const rateValueCells = rateColumns
        .map(
          (col) => `
            <td style="width: 24.8855%;">
                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${col.value} RON</span></div>
                <div style="text-align: center;"><span style="color: #000000;"><sup><span style="font-size: 12px;">RON/lună</span></sup></span></div>
            </td>`
        )
        .join("");

      layawayHtml = `
[vc_toggle title="Doresc produsul prin LAYAWAY" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790924554{margin-top: -30px !important;}"]
<table style="height: 133px;">
    <tbody>
        <tr>
            <td style="width: 25.0000%;">
                <div style="text-align: center;"><span style="color: #000000;"><strong><span style="font-family: Symbol; font-size: 14px;">Avans 10%</span></strong></span></div>
            </td>
            ${rateHeaderCells}
        </tr>
        <tr>
            <td style="width: 25.0000%;">
                <div style="text-align: center;"><span style="font-size: 12px; color: #000000;">${advance} RON</span></div>
            </td>
            ${rateValueCells}
        </tr>
    </tbody>
</table>
<p style="text-align: center;">COMISION <strong>0%</strong></p>
<p style="text-align: center;" align="center">Acum plătești doar avansul iar noi o să te contactăm în maxim 4 ore pentru semnarea contractului. Totul este online și rapid.</p>
[/vc_toggle]
`.trim();
    }
  }

  const layawayBlock = layawayHtml ? `${layawayHtml}\n[vc_separator]\n` : "";
  const safeIncludedItems = Array.isArray(includedItems) ? includedItems : [];
  let resolvedConditionLabel = "Pre-owned";
  const isAccessoryApparel =
    Array.isArray(apparelMeasurements) && apparelMeasurements.length > 0;
  const isAccessoryWrap = isAccessoryScarf || isAccessoryHat || isAccessoryApparel;

  const renderSectionGroup = (
    label,
    value,
    { width = "50%", paddingRight = "15px" } = {}
  ) => `
    <div class="pdp__section-group " style="-webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px ${paddingRight} 0px 0px; width: ${width};">
        <div class="pdp__group-title" style="-webkit-font-smoothing: antialiased; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">${label}</div>
        <div class="pdp__group-item" style="-webkit-font-smoothing: antialiased; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${value || "—"}</div>
    </div>`;

  const ensurePreownedParagraph = (content) => {
    if (!content) return "";
    return content.includes("lpoi-preowned")
      ? content
      : `${content}\n<p class="lpoi-preowned">Acest articol este un produs <span class="s1"><b>${resolvedConditionLabel}</b></span> autentic.</p>`;
  };

  if (isAccessoryWrap) {
    const wrapLabel = isAccessoryHat
      ? "pălărie"
      : isAccessoryApparel
        ? "piesă vestimentară"
        : "eșarfă";
    const wrapLabelCapitalized = isAccessoryHat
      ? "Pălăria"
      : isAccessoryApparel
        ? "Piesa vestimentară"
        : "Eșarfa";
    const includedBlock =
      safeIncludedItems.length
        ? safeIncludedItems.map((item) => `<div>${item}</div>`).join("")
        : "<div>—</div>";
    const specificsContent = ensurePreownedParagraph(
      customDescription?.trim() ||
        `${wrapLabelCapitalized} este într-o stare excelentă și completează perfect orice ținută.`
    );
    const widthLabel = isAccessoryHat ? "Înălțime" : "Lățime";
    const lengthLabel = isAccessoryHat ? "Circumferință" : "Lungime";
    let dimensionHtml = "—";
    if (isAccessoryApparel) {
      const apparelLines = apparelMeasurements
        .map(({ label, value }) => {
          const lower = (label || "").toLowerCase();
          const formatted =
            lower.includes("mărime") || lower.includes("marime")
              ? String(value || "").trim()
              : formatDimensionValue(value);
          return formatted ? `${label}: ${formatted}` : "";
        })
        .filter(Boolean);
      dimensionHtml = apparelLines.length ? apparelLines.join("<br/>") : "—";
    } else {
      const dimensionLines = [];
      if (isAccessoryHat) {
        if (itemSize) {
          dimensionLines.push(`Mărime: ${itemSize}`);
        }
        if (length) {
          dimensionLines.push(
            `${lengthLabel}: ${formatDimensionValue(length)}`
          );
        }
        if (height) {
          dimensionLines.push(`${widthLabel}: ${formatDimensionValue(height)}`);
        }
      } else {
        if (length) {
          dimensionLines.push(
            `${lengthLabel}: ${formatDimensionValue(length)}`
          );
        }
        if (height) {
          dimensionLines.push(`${widthLabel}: ${formatDimensionValue(height)}`);
        }
      }
      dimensionHtml = dimensionLines.length
        ? dimensionLines.join("<br/>")
        : "—";
    }
    const accessoryDetailsGrid = `
<div class="pdp__section-content" style="-webkit-font-smoothing: antialiased; margin: 0px; padding: 0px; display: flex; flex-flow: row wrap; color: #000000; font-family: MaisonNeue-Book; font-size: 14px;">
${renderSectionGroup("Culoare", exteriorColor || "—")}
${renderSectionGroup("Material", exteriorMaterialBlock || exteriorMaterial || "—")}
${renderSectionGroup("Vine însoțit", includedBlock || "—")}
${renderSectionGroup("Designer ID", designerId || "—")}
${renderSectionGroup("Locație", location || "—")}
${renderSectionGroup("Stare", resolvedConditionLabel || "—", {
      width: "50%",
      paddingRight: "0px",
    })}
</div>
<span style="font-size: 18px; font-weight: 500; color: #767676;">Mărime &amp; Dimensiuni</span><br/>
${dimensionHtml}`.trim();

    const brandDisclaimer = `<p style="font-size: 10px; color: #595959; font-family: Metropolis, 'Helvetica Neue', Helvetica, Arial, sans-serif;">
${brand} este marcă înregistrată. Luxura Elite nu este afiliată cu ${brand}.
</p>`;

    return `
[vc_row]
[vc_column]

[vc_column_text]
[range_shortcode val="${conditionValue}"]
[/vc_column_text]

[vc_toggle title="✩ Caracteristici Specifice" custom_font_container="tag:p|font_size:18|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" class="fa"]
${specificsContent}
[/vc_toggle]

[vc_toggle title="DETALII" open="true" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true"]
${accessoryDetailsGrid}
[/vc_toggle]

[vc_separator]

[vc_toggle title="DESCRIERE" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790907923{margin-top: -30px !important;}"]
${ensurePreownedParagraph(detailedDescription || specificsContent)}
[/vc_toggle]

[vc_separator]

[vc_toggle title="LIVRARE & RETUR" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790915357{margin-top: -30px !important;}"]
<p style="text-align: justify;">Expedierea internă standard în Romania este <strong>GRATUITĂ.</strong></p>
<p style="text-align: justify;">Transportul include: <strong>asigurare, confirmare semnătură și ambalaj de calitate superioară</strong>. Toate articolele vor fi expediate, în general, în termen de 3 zile lucrătoare de la confirmarea plății.</p>
<p style="text-align: justify;">Returnează: Da! Articolul poate fi returnat în termen de 14 zile de la achiziție, cu etichetele de siguranță atașate și în starea inițială.</p>
${
  !isFootwear && sortlyId
    ? `<div><span class="sc-beySbM jyPPDw">${sortlyId}</span></div>`
    : ""
}
[/vc_toggle]

[vc_separator]

${layawayBlock}

[/vc_column]
[/vc_row]

${brandDisclaimer}`.trim();
  }

  const brandDisclaimer = `<p style="font-size: 10px; color: #595959; font-family: Metropolis, 'Helvetica Neue', Helvetica, Arial, sans-serif;">
${brand} este marcă înregistrată. Luxura Elite nu este afiliată cu ${brand}.
</p>`;

  return `
[vc_row]
[vc_column]

[vc_column_text]
[range_shortcode val="${conditionValue}"]
[/vc_column_text]

[vc_toggle title="✩ Caracteristici Specifice" custom_font_container="tag:p|font_size:18|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" class="fa"]
${customDescription || "Pre-owned nepurtat, nu prezintă absolut niciun semn de uzură."}
[/vc_toggle]

[vc_separator]

[vc_toggle title="DETALII" open="true" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true"]
<div class="pdp__section-content" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px; padding: 0px; display: flex; flex-flow: row wrap; color: #000000; font-family: MaisonNeue-Book; font-size: 14px; font-style: normal; font-variant-ligatures: normal; font-variant-caps: normal; font-weight: 400; letter-spacing: normal; orphans: 2; text-align: left; text-indent: 0px; text-transform: none; white-space: normal; widows: 2; word-spacing: 0px; -webkit-text-stroke-width: 0px; background-color: #ffffff; text-decoration-thickness: initial; text-decoration-style: initial; text-decoration-color: initial;">
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px 15px 0px 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Culoare Exterioară</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${exteriorColor}</div>
    </div>
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Culoare Interioară</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${interiorColor}</div>
    </div>
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px 15px 0px 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Material Exterior</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${exteriorMaterialBlock || exteriorMaterial || "—"}</div>
    </div>
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Material Interior</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${interiorMaterialBlock || interiorMaterial || "—"}</div>
    </div>
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Vine însoțit</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">
            ${safeIncludedItems.map((item) => `<div>${item}</div>`).join("")}
        </div>
    </div>
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px 15px 0px 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Culoare Hardware</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${hardwareColor}</div>
    </div>
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Designer ID</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${designerId}</div>
    </div>
    ${
      !isFootwear && entrupyId
        ? `
    <div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px 15px 0px 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Entrupy ID</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${entrupyId}</div>
    </div>`
        : ""
    }
<div class="pdp__section-group" style="box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px 15px 0px 0px; width: 50%;">
        <div class="pdp__group-title" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Locație</div>
        <div class="pdp__group-item" style="box-sizing: border-box; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${location}</div>
    </div>
    <div class="pdp__section-group" style="-webkit-font-smoothing: antialiased; margin: 0px 0px 25px; padding: 0px; width: 50%;">
        <div class="pdp__group-title" style="-webkit-font-smoothing: antialiased; margin: 0px; padding: 0px; font-family: MaisonNeue-Demi, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #767676; letter-spacing: 0px; line-height: 24px; display: inline-block;">Stare</div>
        <div class="pdp__group-item" style="-webkit-font-smoothing: antialiased; margin: 0px; padding: 0px; font-family: MaisonNeue-Book, Helvetica, Arial, sans-serif; font-size: 0.875rem; color: #000000; letter-spacing: 0px; line-height: 24px;">${resolvedConditionLabel}</div>
    </div>
</div>
<span style="font-size: 18px; font-weight: 500; color: #767676;">Mărime &amp; Dimensiuni</span><br>
  ${
    isFootwear
      ? `Mărime: ${plainValueOrDash(cleanedItemSize || length)}<br>Centimetri: ${dimensionOrDash(
          height
        )}${
          cleanedFootwearNote
            ? `<br>Observații mărime: ${cleanedFootwearNote}`
            : ""
        }`
      : `Marime: ${plainValueOrDash(cleanedSizeValue)}<br>Lungimea: ${dimensionOrDash(
          length
        )}<br>Inălțime: ${dimensionOrDash(height)}<br>Adâncime: ${dimensionOrDash(
          depth
        )}`
  }
[/vc_toggle]
[vc_separator]

[vc_toggle title="DESCRIERE" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790907923{margin-top: -30px !important;}"]
<p style="text-align: justify;">${detailedDescription}</p>
[/vc_toggle]

[vc_separator]

[vc_toggle title="LIVRARE & RETUR" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790915357{margin-top: -30px !important;}"]
<p style="text-align: justify;">Expedierea internă standard în Romania este <strong>GRATUITĂ.</strong></p>
<p style="text-align: justify;">Transportul include: <strong>asigurare, confirmare semnătură și ambalaj de calitate superioară</strong>. Toate articolele vor fi expediate, în general, în termen de 3 zile lucrătoare de la confirmarea plății.</p>
<p style="text-align: justify;">Returnează: Da! Articolul poate fi returnat în termen de 14 zile de la achiziție, cu etichetele de siguranță atașate și în starea inițială.</p>
${
  !isFootwear && sortlyId
    ? `<div><span class="sc-beySbM jyPPDw">${sortlyId}</span></div>`
    : ""
}
[/vc_toggle]

[vc_separator]

${layawayBlock}

[/vc_column]
[/vc_row]

${brandDisclaimer}
`;
};

export default buildHtmlContent;
